import React, { lazy, Suspense, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeroWithStats } from "@/components/blocks/HeroWithStats"
import { FestCard } from "@/components/ui/fest-card"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { 
  Globe, 
  Users, 
  Calendar, 
  Award,
  ArrowRight,
  Mail,
  Search,
  CheckCircle,
  Edit
} from "lucide-react"
import communityImage from "@/assets/community-image.jpg"
import { useFestivals } from "@/hooks/useFestivals"
import { useOrganizations } from "@/hooks/useOrganizations"
import { useLandingHeroContent } from "@/hooks/useLandingHeroContent"
import { useEditMode } from "@/components/EditModeProvider"

// Lazy load heavy gallery component  
const WebImageGallery = lazy(() => import("@/components/WebImageGallery").then(module => ({ default: module.WebImageGallery })))


const Home = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const { festivals, loading, error } = useFestivals()
  const { organizations, loading: orgsLoading } = useOrganizations()
  const { data: landing, loading: landingLoading } = useLandingHeroContent()
  const { isEditMode } = useEditMode()

  // Filter for homepage: only our own events + nomad fests (not external workations from other providers)
  // External workations from other providers are only shown on the /events detail page
  const sortedFestivals = [...festivals]
    .filter(festival => {
      const startDate = new Date(festival.start_date || '');
      const isUpcoming = startDate >= new Date() && festival.status !== 'past';
      if (!isUpcoming) return false;
      
      // Show on homepage: our own events OR nomad-fest themed events
      const isOurEvent = festival.is_featured || (festival as any).organized_by_us === true;
      const isNomadFest = festival.theme === 'nomad-fest';
      return isOurEvent || isNomadFest;
    })
    .sort((a, b) => {
      const dateA = new Date(a.start_date || '');
      const dateB = new Date(b.start_date || '');
      return dateA.getTime() - dateB.getTime();
    });

  // Transform Supabase data to match FestCard props
  const upcomingFests = sortedFestivals.map(festival => ({
    slug: festival.slug || festival.id, // Using slug or ID as slug
    title: festival.name,
    titleEn: festival.name_en,
    description: festival.description || "Join us for an amazing nomad festival experience",
    descriptionEn: festival.description_en,
    location: festival.location || "TBA",
    locationEn: festival.location_en,
    startDate: festival.start_date || "",
    endDate: festival.end_date || "",
    imageUrl: festival.image_url || communityImage,
    attendeeCount: festival.current_participants,
    ticketPrice: (festival.price_display?.trim()?.toLowerCase() === 'free' ? 'Get Tickets' : (festival.price_display || 'Get Tickets')),
    status: "upcoming" as const,
    featured: festival.is_featured,
    isExternal: festival.is_external,
    externalLink: festival.external_link || festival.website_url || undefined,
    heroStats: festival.hero_stats as any,
  }))

  const filteredFests = useMemo(() => {
    if (!eventSearch.trim()) return upcomingFests;
    const q = eventSearch.toLowerCase();
    return upcomingFests.filter(f =>
      f.title.toLowerCase().includes(q) ||
      f.description.toLowerCase().includes(q) ||
      f.location.toLowerCase().includes(q)
    );
  }, [upcomingFests, eventSearch]);

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Product Designer",
      company: "Stripe",
      quote: "Nomad Fest connected me with my current co-founder. The networking opportunities are unmatched.",
      avatar: "SC"
    },
    {
      name: "Marcus Rodriguez",
      role: "Software Engineer",
      company: "Shopify",
      quote: "The workshops and talks completely transformed how I approach remote work. Highly recommend!",
      avatar: "MR"
    },
    {
      name: "Elena Kozlova",
      role: "Marketing Director",
      company: "Buffer",
      quote: "Three years attending Nomad Fest events. Each one has been instrumental in my career growth.",
      avatar: "EK"
    }
  ]

  // Check if we have landing hero content that should override event data
  const hasLandingOverrides = !!(landing && (landing.title || (landing.stats && landing.stats.length > 0)));

  return (
    <>
      <SEOHead
        description="Join thousands of digital nomads, entrepreneurs, and remote workers at unforgettable festivals, retreats, and community events worldwide."
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Nomad Fest',
          url: 'https://nomad-fest.com',
          logo: 'https://nomad-fest.com/og-default.png',
          description: 'Connect with digital nomads, entrepreneurs, and remote workers at unforgettable festivals, retreats, and community events worldwide.',
          sameAs: [
            'https://www.linkedin.com/company/nomadfest',
            'https://www.instagram.com/nomadfestofficial',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            url: 'https://nomad-fest.com/contact',
          },
        }}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative">
        {/* Edit Landing Hero Button - Only show when landing overrides are active */}
        {isEditMode && hasLandingOverrides && (
          <Button
            onClick={() => navigate('/admin?tab=landing-hero')}
            className="absolute top-20 right-4 z-20 gap-2 shadow-lg"
            variant="secondary"
            size="sm"
          >
            <Edit className="h-4 w-4" />
            <span className="hidden sm:inline">Edit Landing Hero</span>
          </Button>
        )}
        
        {/* Show skeleton while loading to prevent flash of default content */}
        {(loading || landingLoading) ? (
          <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary via-primary-glow to-secondary">
            <div className="absolute inset-0 bg-gradient-subtle opacity-50" />
            <div className="relative z-10 text-center space-y-4 animate-pulse px-4">
              <div className="h-16 w-3/4 mx-auto bg-background/20 rounded-lg" />
              <div className="h-8 w-1/2 mx-auto bg-background/20 rounded-lg" />
              <div className="h-6 w-2/3 mx-auto bg-background/20 rounded-lg" />
            </div>
          </div>
        ) : (
          <HeroWithStats
          eventId={hasLandingOverrides ? undefined : upcomingFests[0]?.slug}
          eventType="nomad-fest"
          title={hasLandingOverrides ? (landing.title || "The Nomad Fest Movement") : (upcomingFests[0]?.title || "Connect. Create. Celebrate.")}
          subtitle={hasLandingOverrides ? (landing.subtitle || "Festivals · Workations · AI Fest · Academies") : (upcomingFests[0]?.location || "World's Leading Nomad Community")}
          description={hasLandingOverrides ? (landing.description || "") : (upcomingFests[0]?.description || "Join thousands of digital nomads, entrepreneurs, and remote workers at unforgettable festivals worldwide.")}
          primaryCTA={hasLandingOverrides ? (landing.primary_cta || "Explore Events") : "Get Your Tickets"}
          secondaryCTA={hasLandingOverrides ? (landing.secondary_cta || "Learn More") : "Learn More"}
          primaryCTALink={hasLandingOverrides ? landing.primary_cta_link : undefined}
          secondaryCTALink={hasLandingOverrides ? landing.secondary_cta_link : undefined}
          backgroundVideoUrl={hasLandingOverrides ? landing.background_video_url : undefined}
          disableInlineEdit={hasLandingOverrides}
          cyclingWords={hasLandingOverrides ? [] : undefined}
          phrases={hasLandingOverrides ? [
            "Meet your next business partner",
            "Find your future co-founder",
            "Learn from world-class entrepreneurs",
            "Connect with 5,000+ global nomads",
            "Get inspired to live life on your terms",
            "Discover your next home base",
            "Grow your network across 30+ countries",
            "Join the movement redefining work & freedom",
          ] : undefined}
          stats={
            hasLandingOverrides && landing.stats && landing.stats.length > 0
              ? landing.stats
              : upcomingFests.length > 0 
                ? (
                    // Use custom hero_stats from database if available, otherwise calculate from event data
                    upcomingFests[0].heroStats && Array.isArray(upcomingFests[0].heroStats) && upcomingFests[0].heroStats.length > 0
                      ? upcomingFests[0].heroStats
                      : [
                          {
                            value: upcomingFests[0].startDate && upcomingFests[0].endDate 
                              ? String(Math.ceil((new Date(upcomingFests[0].endDate).getTime() - new Date(upcomingFests[0].startDate).getTime()) / (1000 * 3600 * 24)) + 1)
                              : '8',
                            label: 'Days'
                          },
                          {
                            value: String(upcomingFests[0].attendeeCount || '50+'),
                            label: 'Participants'
                          },
                          {
                            value: upcomingFests[0].location || 'Global',
                            label: 'Location'
                          },
                          {
                            value: 'Epic',
                            label: 'Experience'
                          }
                        ]
                  )
                : [
                    {
                      value: '10K+',
                      label: 'Members'
                    },
                    {
                      value: '50+',
                      label: 'Cities'
                    },
                    {
                      value: '100+',
                      label: 'Events'
                    },
                    {
                      value: '95%',
                      label: 'Satisfaction'
                    }
                  ]
          }
        />
        )}
      </div>


      {/* Upcoming Fests Section */}
      <section className="py-20 bg-gradient-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary">
              Coming Soon
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Upcoming Nomad Fests & Events
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From global nomad festivals to mountain retreats in Switzerland - discover experiences crafted for connection and growth.
            </p>
          </div>

          {/* Search field */}
          <div className="max-w-md mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events by name, location..."
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-96 bg-muted rounded-lg animate-pulse" />
              ))
            ) : error ? (
              <div className="col-span-full text-center text-muted-foreground">
                Failed to load festivals: {error}
              </div>
            ) : filteredFests.length > 0 ? (
              filteredFests.slice(0, 6).map((fest) => (
                <FestCard key={fest.slug} {...fest} />
              ))
            ) : (
              <div className="col-span-full text-center text-muted-foreground">
                {eventSearch ? `No events found for "${eventSearch}"` : 'No upcoming festivals found'}
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => navigate('/our-events')}>
              <Calendar className="mr-2 h-5 w-5" />
              Our Events
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate('/nomad-fests-worldwide')}>
              <Globe className="mr-2 h-5 w-5" />
              Nomad Fests Worldwide
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/events')}>
              All Events
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge variant="secondary" className="mb-4 bg-secondary/20 text-secondary">
                Our Community
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                More Than Just Events
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                We're building the world's most supportive nomad community. From first-time remote workers to seasoned entrepreneurs, everyone finds their tribe here.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Global Network</h3>
                    <p className="text-sm text-muted-foreground">Connect across continents</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-secondary/10 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Curated Community</h3>
                    <p className="text-sm text-muted-foreground">Quality over quantity</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-accent/10 p-2 rounded-lg">
                    <Calendar className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Year-Round Events</h3>
                    <p className="text-sm text-muted-foreground">Always something happening</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Award className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Expert-Led</h3>
                    <p className="text-sm text-muted-foreground">Learn from the best</p>
                  </div>
                </div>
              </div>
              
              <Button variant="hero" onClick={() => navigate('/events')}>
                Join Our Community
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <div className="relative">
              <img
                src={communityImage}
                alt="Community members networking"
                className="aspect-[4/3] rounded-2xl object-cover shadow-festival w-full"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-card">
                <div className="flex items-center space-x-4">
                  <div className="bg-gradient-hero p-3 rounded-full">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-lg">2,500+</div>
                    <div className="text-sm text-muted-foreground">New connections made</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4 bg-accent/20 text-accent">
              Community Love
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What Our Community Says
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/50 backdrop-blur-sm border-0 shadow-card">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-hero rounded-full flex items-center justify-center text-white font-bold mr-4">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {testimonial.role} at {testimonial.company}
                      </div>
                    </div>
                  </div>
                  <p className="text-muted-foreground italic">
                    "{testimonial.quote}"
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery - Simplified lazy loading */}
      <div className="min-h-[400px]">
        <Suspense fallback={
          <div className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-48 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            </div>
          </div>
        }>
          <WebImageGallery />
        </Suspense>
      </div>

      {/* Newsletter */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="secondary" className="mb-4 bg-primary/10 text-primary">
            Stay Connected
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Never Miss a Festival
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Get early access to tickets, exclusive content, and nomad community updates.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input 
              placeholder="Enter your email" 
              className="flex-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button 
              variant="hero"
              onClick={() => {
                if (email && email.includes('@')) {
                  toast.success('Thanks for subscribing! Check your email for confirmation.');
                  setEmail('');
                } else {
                  toast.error('Please enter a valid email address');
                }
              }}
            >
              <Mail className="mr-2 h-4 w-4" />
              Subscribe
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mt-4">
            Join 10,000+ nomads getting our newsletter. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg font-semibold text-muted-foreground mb-8">
              Trusted by Leading Organizations
            </h3>
            {orgsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
                {organizations.map((org) => (
                  <div key={org.id} className="flex items-center justify-center">
                    {org.logo_url ? (
                      org.website_url ? (
                        <a 
                          href={org.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center"
                        >
                          <img 
                            src={org.logo_url} 
                            alt={org.name}
                            className="h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <img 
                          src={org.logo_url} 
                          alt={org.name}
                          className="h-12 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="text-sm font-medium opacity-60">{org.name}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
    </>
  )
}

export default Home